// ============================================
// Orders Hooks - TanStack Query
// ============================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import type {
  CreateOrderRequest,
  UpdateOrderRequest,
  OrderListParams,
  OrderStatus,
  VerifyCartRequest,
  UUID,
} from '@conai/shared-types';
import { useApi, useOrdersService } from '../providers/ApiProvider';
import { queryKeys } from '../config/query-client';

/**
 * Response của GET /orders/stats/ — nguồn đếm đơn CHUẨN toàn app.
 * BE zero-fill đủ 11 status và dùng chung get_queryset với /orders/, nên số
 * luôn khớp với bảng Orders (cùng scope, cùng soft-delete).
 */
export interface OrderStatsResponse {
  status_counts: Record<string, number>;
  countable_orders: number;
  void_orders: number;
  total_orders: number;
  revenue_orders: number;
  revenue: string;
}

export function useOrderStats(
  params: { store_id?: string; start_date?: string; end_date?: string } = {},
  options?: { enabled?: boolean }
) {
  const api = useApi();

  return useQuery({
    queryKey: ['orders', 'stats', params] as const,
    queryFn: () =>
      api.client.get<OrderStatsResponse>('/orders/stats/', {
        store_id: params.store_id,
        start_date: params.start_date,
        end_date: params.end_date,
      }),
    enabled: options?.enabled !== false,
  });
}

/**
 * Thử một giỏ mẫu để xem engine khuyến mãi áp cái gì (sheet 2 mục #41).
 *
 * `useMutation` chứ không phải `useQuery`: đây là hành động người dùng bấm
 * "Thử", không phải dữ liệu tự nạp theo màn hình — và không nên cache lại một
 * giỏ mẫu tạm.
 */
export function useVerifyCart() {
  const ordersService = useOrdersService();

  return useMutation({
    mutationFn: (data: VerifyCartRequest) => ordersService.verifyCart(data),
  });
}

/**
 * Hook to list orders with pagination and filters
 */
export function useOrders(params: OrderListParams = {}) {
  const ordersService = useOrdersService();

  return useQuery({
    queryKey: queryKeys.orders.list(params as Record<string, unknown>),
    queryFn: () => ordersService.list(params),
  });
}

// NOTE: hook useAllOrders (fetch-all lật mọi trang) đã bị xoá có chủ đích —
// DB nhiều đơn là mỗi lần mở trang bắn hàng chục request. Danh sách dùng
// phân trang server-side (useOrders với page/page_size), số đếm dùng useOrderStats.

/**
 * Hook to list orders for a specific store
 */
export function useStoreOrders(
  storeId: UUID | undefined,
  params: Omit<OrderListParams, 'store'> = {}
) {
  const ordersService = useOrdersService();

  return useQuery({
    queryKey: queryKeys.orders.list({ ...params, store: storeId }),
    queryFn: () => ordersService.listByStore(storeId!, params),
    enabled: !!storeId,
  });
}

/**
 * Hook to list orders for a specific customer
 */
export function useCustomerOrders(
  customerId: UUID | undefined,
  params: Omit<OrderListParams, 'customer'> = {}
) {
  const ordersService = useOrdersService();

  return useQuery({
    queryKey: queryKeys.orders.byCustomer(customerId || ''),
    queryFn: () => ordersService.listByCustomer(customerId!, params),
    enabled: !!customerId,
  });
}

/**
 * Hook to list orders by status
 */
export function useOrdersByStatus(
  status: OrderStatus,
  params: Omit<OrderListParams, 'status'> = {}
) {
  const ordersService = useOrdersService();

  return useQuery({
    queryKey: queryKeys.orders.list({ ...params, status }),
    queryFn: () => ordersService.listByStatus(status, params),
  });
}

/**
 * Hook to get a single order by ID
 */
export function useOrder(id: string, options?: { enabled?: boolean }) {
  const ordersService = useOrdersService();

  return useQuery({
    queryKey: queryKeys.orders.detail(id),
    queryFn: () => ordersService.getById(id),
    enabled: options?.enabled !== false && !!id,
  });
}

/**
 * Hook to get order items
 */
export function useOrderItems(orderId: string, options?: { enabled?: boolean }) {
  const ordersService = useOrdersService();

  return useQuery({
    queryKey: [...queryKeys.orders.detail(orderId), 'items'],
    queryFn: () => ordersService.getItems(orderId),
    enabled: options?.enabled !== false && !!orderId,
  });
}

/**
 * Hook to create a new order
 */
export function useCreateOrder() {
  const ordersService = useOrdersService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateOrderRequest) => ordersService.create(data),
    onSuccess: (newOrder) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
      // Dashboard stats (Total Orders, Operations Overview) are derived from the
      // reports/* endpoints — refresh them so a newly created order is reflected.
      queryClient.invalidateQueries({ queryKey: queryKeys.reports.all });
      toast.success(`Order ${newOrder.order_number} created`);
    },
  });
}

/**
 * Hook to update an order
 */
export function useUpdateOrder() {
  const ordersService = useOrdersService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateOrderRequest }) =>
      ordersService.update(id, data),
    onSuccess: (updatedOrder) => {
      queryClient.setQueryData(queryKeys.orders.detail(updatedOrder.id), updatedOrder);
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.lists() });
      toast.success(`Order ${updatedOrder.order_number} updated`);
    },
  });
}

/**
 * Hook to confirm an order (PENDING -> CONFIRMED)
 */
export function useConfirmOrder() {
  const ordersService = useOrdersService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => ordersService.confirm(id),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(result.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.lists() });
      toast.success('Order confirmed');
    },
  });
}

/**
 * Hook to cancel an order
 */
export function useCancelOrder() {
  const ordersService = useOrdersService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      ordersService.cancel(id, reason),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(result.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
      toast.success('Order cancelled');
    },
  });
}

/**
 * Hook to start processing an order (CONFIRMED -> PROCESSING)
 */
export function useStartProcessingOrder() {
  const ordersService = useOrdersService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => ordersService.startProcessing(id),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(result.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.lists() });
      toast.success('Order is now processing');
    },
  });
}

/**
 * Hook to mark order as ready (PROCESSING -> READY)
 */
export function useMarkOrderReady() {
  const ordersService = useOrdersService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => ordersService.markReady(id),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(result.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.lists() });
      toast.success('Order is ready');
    },
  });
}

/**
 * Hook to complete an order (READY -> COMPLETED)
 */
export function useCompleteOrder() {
  const ordersService = useOrdersService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => ordersService.complete(id),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(result.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
      toast.success('Order completed');
    },
  });
}

/**
 * Hook to get recent orders
 */
export function useRecentOrders(storeId?: UUID, limit = 10) {
  const ordersService = useOrdersService();

  return useQuery({
    queryKey: [...queryKeys.orders.lists(), 'recent', storeId, limit],
    queryFn: () => ordersService.getRecent(storeId, limit),
  });
}

/**
 * Hook to get pending orders
 */
export function usePendingOrders(storeId?: UUID, params?: { page?: number; page_size?: number }) {
  const ordersService = useOrdersService();

  return useQuery({
    queryKey: [...queryKeys.orders.lists(), 'pending', storeId, params],
    queryFn: () => ordersService.getPending(storeId, params),
    // Refetch every 30 seconds to catch new pending orders
    refetchInterval: 30 * 1000,
  });
}

/**
 * Hook to get today's orders
 */
export function useTodayOrders(storeId?: UUID, params?: { page?: number; page_size?: number }) {
  const ordersService = useOrdersService();

  return useQuery({
    queryKey: [...queryKeys.orders.lists(), 'today', storeId, params],
    queryFn: () => ordersService.getToday(storeId, params),
    // Refetch every minute
    refetchInterval: 60 * 1000,
  });
}

// NOTE: bản useOrderStats cũ (đếm client-side qua 3 query count) đã bị xoá —
// dùng bản ở đầu file gọi GET /orders/stats/ (BE đếm, cùng scope với /orders/).

/**
 * Get order status display info
 */
export function getOrderStatusInfo(status: OrderStatus): {
  label: string;
  color: string;
  bgColor: string;
} {
  switch (status) {
    case 'DRAFT':
      return {
        label: 'Draft',
        color: 'text-slate-600',
        bgColor: 'bg-slate-100',
      };
    case 'PENDING_PAYMENT':
      return {
        label: 'Pending Payment',
        color: 'text-amber-700',
        bgColor: 'bg-amber-100',
      };
    case 'CONFIRMED':
      return {
        label: 'Confirmed',
        color: 'text-blue-700',
        bgColor: 'bg-blue-100',
      };
    case 'PREPARING':
      return {
        label: 'Preparing',
        color: 'text-indigo-700',
        bgColor: 'bg-indigo-100',
      };
    case 'READY':
      return {
        label: 'Ready',
        color: 'text-cyan-700',
        bgColor: 'bg-cyan-100',
      };
    case 'PAID':
      return {
        label: 'Paid',
        color: 'text-emerald-700',
        bgColor: 'bg-emerald-100',
      };
    case 'COMPLETED':
      return {
        label: 'Completed',
        color: 'text-green-700',
        bgColor: 'bg-green-100',
      };
    case 'CANCELLED':
      return {
        label: 'Cancelled',
        color: 'text-red-700',
        bgColor: 'bg-red-100',
      };
    case 'VOIDED':
      return {
        label: 'Voided',
        color: 'text-red-700',
        bgColor: 'bg-red-50',
      };
    case 'REFUNDED':
      return {
        label: 'Refunded',
        color: 'text-orange-700',
        bgColor: 'bg-orange-100',
      };
    case 'PARTIALLY_REFUNDED':
      return {
        label: 'Partially Refunded',
        color: 'text-orange-700',
        bgColor: 'bg-orange-50',
      };
    default:
      return {
        label: status,
        color: 'text-gray-700',
        bgColor: 'bg-gray-100',
      };
  }
}
